import { useMemo, useState, useEffect } from 'react';
import { ArrowUpDown, ChevronLeft, ChevronRight, ArrowDownToLine, ArrowUpFromLine, Battery, Sun, Zap, Fuel, Leaf, Settings, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { getDemoSoc } from '@/lib/demoData';
import { currentSupabaseEnv } from '@/lib/supabase';
import type { StationMetricsRow, StationMetricsTableData } from '@/types/reports';

interface StationMetricsTableProps {
  data: StationMetricsTableData;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSort?: (column: keyof StationMetricsRow, direction: 'asc' | 'desc') => void;
}

type SortColumn = keyof StationMetricsRow;
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const STORAGE_KEY = 'stationMetrics_visibleColumns';

// Column configuration - all icons use same muted color for consistency
const COLUMNS: {
  key: SortColumn | '#';
  label: string;
  shortLabel: string;
  icon?: React.ElementType;
  format: (value: number | string | null) => string;
  unit?: string;
  align: 'left' | 'right';
  isRowNumber?: boolean;
  className?: string;
  hideable?: boolean; // Can this column be hidden?
  totalsKey?: string; // Key in totals object (for dynamic rendering)
}[] = [
  {
    key: '#',
    label: '#',
    shortLabel: '#',
    format: (v) => String(v),
    align: 'left',
    isRowNumber: true,
    className: 'w-8 pr-0',
    hideable: false,
  },
  {
    key: 'unitCode',
    label: 'Station',
    shortLabel: 'Station',
    format: (v) => String(v ?? '-'),
    align: 'left',
    className: 'pl-1',
    hideable: false, // Station should always be visible
    totalsKey: 'label',
  },
  {
    key: 'loadKwh',
    label: 'Load',
    shortLabel: 'Load',
    icon: Zap,
    format: (v) => v == null ? '0' : Math.round(Number(v)).toLocaleString(),
    unit: 'kWh',
    align: 'right',
    hideable: true,
    totalsKey: 'loadKwh',
  },
  {
    key: 'batteryLevel',
    label: 'SOC',
    shortLabel: 'SOC',
    icon: Battery,
    // null: a unit that has moved on to another company (F04): its live battery is theirs.
    format: (v) => v == null ? '—' : String(Number(v)),
    unit: '%',
    align: 'right',
    hideable: true,
    totalsKey: 'avgBatterySoc',
  },
  {
    key: 'batteryChargeKwh',
    label: 'Charge',
    shortLabel: 'Chg',
    icon: Battery,
    format: (v) => v == null ? '0' : Math.round(Number(v)).toLocaleString(),
    unit: 'kWh',
    align: 'right',
    hideable: true,
    totalsKey: 'batteryChargeKwh',
  },
  {
    key: 'batteryDischargeKwh',
    label: 'Discharge',
    shortLabel: 'Dis',
    icon: Battery,
    format: (v) => v == null ? '0' : Math.round(Number(v)).toLocaleString(),
    unit: 'kWh',
    align: 'right',
    hideable: true,
    totalsKey: 'batteryDischargeKwh',
  },
  {
    key: 'pvKwh',
    label: 'PV Gen',
    shortLabel: 'PV',
    icon: Sun,
    format: (v) => v == null ? '0' : Math.round(Number(v)).toLocaleString(),
    unit: 'kWh',
    align: 'right',
    hideable: true,
    totalsKey: 'pvKwh',
  },
  {
    key: 'gridImportKwh',
    label: 'Grid In',
    shortLabel: 'In',
    icon: ArrowDownToLine,
    format: (v) => v == null ? '0' : Math.round(Number(v)).toLocaleString(),
    unit: 'kWh',
    align: 'right',
    hideable: true,
    totalsKey: 'gridImportKwh',
  },
  {
    key: 'gridExportKwh',
    label: 'Grid Out',
    shortLabel: 'Out',
    icon: ArrowUpFromLine,
    format: (v) => v == null ? '0' : Math.round(Number(v)).toLocaleString(),
    unit: 'kWh',
    align: 'right',
    hideable: true,
    totalsKey: 'gridExportKwh',
  },
  {
    key: 'generatorKwh',
    label: 'Gen',
    shortLabel: 'Gen',
    icon: Fuel,
    format: (v) => v == null ? '0' : Math.round(Number(v)).toLocaleString(),
    unit: 'kWh',
    align: 'right',
    hideable: true,
    totalsKey: 'generatorKwh',
  },
  {
    key: 'co2EmissionKg',
    label: 'CO₂',
    shortLabel: 'CO₂',
    icon: Leaf,
    format: (v) => v == null ? '0' : Math.round(Number(v)).toLocaleString(),
    unit: 'kg',
    align: 'right',
    hideable: true,
    totalsKey: 'co2EmissionKg',
  },
];

// Get default visible columns (all columns)
const getDefaultVisibleColumns = () => new Set(COLUMNS.map(c => c.key));

// Load visible columns from localStorage
const loadVisibleColumns = (): Set<string> => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return new Set(parsed);
    }
  } catch {
    // Ignore parse errors
  }
  return getDefaultVisibleColumns();
};

function getSocColor(soc: number): string {
  if (soc >= 70) return 'text-[#8FB560]';
  if (soc >= 30) return 'text-amber-500';
  return 'text-red-500';
}

export function StationMetricsTable({
  data,
  onPageChange,
  onPageSizeChange,
  onSort
}: StationMetricsTableProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn>('unitCode');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [pageSize, setPageSize] = useState(data.pageSize || 25);
  const [currentPage, setCurrentPage] = useState(data.currentPage || 1);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(loadVisibleColumns);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  // Persist column visibility to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns]);

  // Toggle column visibility
  const toggleColumn = (columnKey: string) => {
    const col = COLUMNS.find(c => c.key === columnKey);
    if (col && col.hideable === false) return; // Can't hide non-hideable columns

    const newVisible = new Set(visibleColumns);
    if (newVisible.has(columnKey)) {
      // Don't allow hiding if only 2 columns left (# + Station are always visible)
      const hideableVisible = Array.from(newVisible).filter(k => {
        const c = COLUMNS.find(col => col.key === k);
        return c?.hideable !== false;
      });
      if (hideableVisible.length > 1) {
        newVisible.delete(columnKey);
      }
    } else {
      newVisible.add(columnKey);
    }
    setVisibleColumns(newVisible);
  };

  const showAllColumns = () => {
    setVisibleColumns(getDefaultVisibleColumns());
  };

  // Filter columns to only visible ones
  const displayColumns = useMemo(() => {
    return COLUMNS.filter(col => visibleColumns.has(col.key));
  }, [visibleColumns]);

  // Sort rows locally if no external handler
  const sortedRows = useMemo(() => {
    if (onSort) return data.rows; // External sorting

    return [...data.rows].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      // A missing value (e.g. the battery of a unit that has moved on, F04) sorts last either way.
      if (aVal == null || bVal == null) {
        return (aVal == null ? 1 : 0) - (bVal == null ? 1 : 0);
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const aNum = Number(aVal);
      const bNum = Number(bVal);
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });
  }, [data.rows, sortColumn, sortDirection, onSort]);

  // Paginate rows locally
  const paginatedRows = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    return sortedRows.slice(startIdx, endIdx);
  }, [sortedRows, currentPage, pageSize]);

  const handleSort = (column: SortColumn) => {
    const newDirection = column === sortColumn && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(newDirection);
    onSort?.(column, newDirection);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    onPageSizeChange?.(newSize);
    onPageChange?.(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    onPageChange?.(newPage);
  };

  const totalPages = data.totalPages || Math.ceil(data.totalUnits / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, data.totalUnits);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#8FB560]" />
            Station Energy Metrics
            <span className="text-muted-foreground/70">({data.totalUnits})</span>
          </CardTitle>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {/* Column visibility dropdown */}
            <DropdownMenu open={columnMenuOpen} onOpenChange={setColumnMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Column visibility">
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-b border-border">
                  Column Visibility
                </div>
                {COLUMNS.filter(col => col.hideable !== false && !col.isRowNumber).map((col) => (
                  <DropdownMenuItem
                    key={col.key}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleColumn(col.key);
                    }}
                    className="cursor-pointer flex items-center gap-2"
                  >
                    {visibleColumns.has(col.key) ? (
                      <Eye className="w-3 h-3 text-[#8FB560]" />
                    ) : (
                      <EyeOff className="w-3 h-3 text-muted-foreground/50" />
                    )}
                    <span className={visibleColumns.has(col.key) ? 'text-foreground' : 'text-muted-foreground'}>
                      {col.label}
                    </span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    showAllColumns();
                  }}
                  className="cursor-pointer border-t border-border text-[#8FB560]"
                >
                  Show All Columns
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <span>Show:</span>
            <div className="flex gap-1">
              {PAGE_SIZE_OPTIONS.map((size) => (
                <Button
                  key={size}
                  variant={pageSize === size ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => handlePageSizeChange(size)}
                >
                  {size}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {displayColumns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      "px-3 py-2 font-medium text-muted-foreground transition-colors",
                      col.align === 'right' ? 'text-right' : 'text-left',
                      col.isRowNumber ? '' : 'cursor-pointer hover:bg-muted/50',
                      col.className
                    )}
                    onClick={() => !col.isRowNumber && handleSort(col.key as SortColumn)}
                  >
                    <div className={cn(
                      "flex items-center gap-1",
                      col.align === 'right' && 'justify-end'
                    )}>
                      {col.icon && (
                        <col.icon className="h-3 w-3 hidden sm:block text-[#8FB560]" />
                      )}
                      <span className="hidden sm:inline">{col.label}</span>
                      <span className="sm:hidden">{col.shortLabel}</span>
                      {!col.isRowNumber && (
                        <ArrowUpDown className={cn(
                          "h-3 w-3 opacity-50",
                          sortColumn === col.key && "opacity-100"
                        )} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={displayColumns.length}
                    className="px-3 py-8 text-center text-sm text-muted-foreground"
                  >
                    0 rows
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, idx) => (
                  <tr
                    key={row.unitId}
                    className={cn(
                      "border-b border-border/50 hover:bg-muted/20 transition-colors",
                      idx % 2 === 0 ? 'bg-transparent' : 'bg-muted/10'
                    )}
                  >
                    {displayColumns.map((col) => {
                      // Row number column uses the index adjusted for pagination
                      const rowNumber = (currentPage - 1) * pageSize + idx + 1;
                      let value = col.isRowNumber ? rowNumber : row[col.key as keyof StationMetricsRow];
                      // Apply demo SOC when in demo mode and battery level is 0
                      if (col.key === 'batteryLevel' && currentSupabaseEnv === 'demo' && value === 0 && row.unitId) {
                        value = getDemoSoc(row.unitId);
                      }
                      return (
                        <td
                          key={col.key}
                          className={cn(
                            "px-3 py-2 tabular-nums",
                            col.align === 'right' ? 'text-right' : 'text-left',
                            col.key === 'batteryLevel' && value != null && getSocColor(Number(value)),
                            col.key === 'batteryLevel' && value == null && 'text-muted-foreground',
                            col.key === 'unitCode' && 'font-medium',
                            col.isRowNumber && 'text-muted-foreground text-xs',
                            col.className
                          )}
                        >
                          {col.format(value)}
                          {col.unit && !(col.key === 'batteryLevel' && value == null) && (
                            <span className="text-[10px] text-muted-foreground ml-0.5">{col.unit}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
            {/* Totals Row - only show if there are rows */}
            {data.totals && paginatedRows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-border bg-muted/40 font-semibold">
                  {displayColumns.map((col) => {
                    // Row number column - empty
                    if (col.isRowNumber) {
                      return <td key={col.key} className="px-3 py-2 w-8 pr-0"></td>;
                    }
                    // Station column - show "TOTALS" label
                    if (col.key === 'unitCode') {
                      return <td key={col.key} className="px-3 py-2 pl-1">TOTALS</td>;
                    }
                    // Data columns - show totals value
                    const totalsKey = col.totalsKey as keyof typeof data.totals;
                    const value = data.totals[totalsKey] ?? 0;
                    const formattedValue = col.key === 'batteryLevel' 
                      ? String(value) 
                      : Number(value).toFixed(1);
                    return (
                      <td key={col.key} className={cn("px-3 py-2 tabular-nums", col.align === 'right' && 'text-right')}>
                        {formattedValue}
                        {col.unit && <span className="text-[10px] text-muted-foreground font-normal ml-0.5">{col.unit}</span>}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <div className="text-xs text-muted-foreground">
            {data.totalUnits === 0
              ? '0 units'
              : `Showing ${startItem}-${endItem} of ${data.totalUnits} units`}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 w-7 p-0 text-xs"
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}

            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
